import type { FunctionalComponent } from "vue";
import type { JSX } from "vue/jsx-runtime";
import {
  EIDStackEvent,
  useIDStack,
  type IStackItem,
  type UseIDStack,
} from "../useIDStack";
import { useDrawer, type UseDrawer } from "../useDrawer";
import { onKeyUp } from "@vueuse/core";
import { useSmoothAnimation, type UseSmoothAnimation } from "../useSmoothAnimation";

export type IDrawer = {
  body: FunctionalComponent<any> | MaybeRef<JSX.Element>;
  topBar?: FunctionalComponent<any> | MaybeRef<JSX.Element>;
  topBarActions?:
    | FunctionalComponent<{ hook: UseDrawer }>
    | MaybeRef<JSX.Element>;
  onClose?: () => any;
  animation: UseSmoothAnimation;
  hook: UseDrawer;
  key?: string;
};

export type IDrawerInit = Omit<IDrawer, "hook" | 'animation'> & { hook?: UseDrawer };

const stack = useIDStack<IDrawer>();

export type UseDrawers = {
  drawers: ComputedRef<Array<IStackItem<IDrawer>>>;
  push: (item: IDrawerInit) => string;
  pop: (id?: string) => void;
  clear: () => void;
  count: ComputedRef<number>;
  stack: UseIDStack<IDrawer>;
};

export const useDrawers = (): UseDrawers => {
  const drawers = computed(() => stack.items.value);
  const count = computed(() => drawers.value.length);
  const exists = new Map<string, string>();
  const idToKey = new Map<string, string>();

  const clear = () => {
    stack.clear();
  };

  const push = (item: IDrawerInit): string => {
    if (item.key) {
      const id = exists.get(item.key);
      if (id) return id;
    }
    
    const animation = useSmoothAnimation({
        initial: 1.0
    });
    const hook = item.hook || useDrawer({
      animation: animation
    });
    const entry = stack.push({
      ...item,
      hook: hook,
      animation: animation
    });

    const onClose = item.onClose;

    if (onClose) {
      const unsub = stack.events.subscribe(EIDStackEvent.REMOVED, async (ev) => {
        if (ev.id === entry.id) {
          await animation.to(0.0, { smoothness: 1.0, duration: 0.4 });
          const key = idToKey.get(entry.id);
          if (key) {
            exists.delete(key);
            idToKey.delete(entry.id);
          }
          onClose();
          queueMicrotask(() => {
            unsub();
          });
        }
      });
    }

    if (item.key) {
      exists.set(item.key, entry.id);
      idToKey.set(entry.id, item.key);
    }
    
    return entry.id;
  };

  const pop = (id?: string) => {
    stack.pop(id);
    if (id) {
      const key = idToKey.get(id);
      if (key) {
        exists.delete(key);
        idToKey.delete(id);
      }
    }
  };

  onKeyUp("Escape", () => {
    pop();
  });

  return {
    push,
    pop,
    clear,
    drawers,
    count,
    stack: stack,
  };
};

export const globalDrawers = useDrawers();

export const clearDrawers = () => {
  globalDrawers.clear();
};

export const pushDrawer = (item: IDrawerInit): string => {
  return globalDrawers.push(item);
};

export const popDrawer = (id?: string) => {
  return globalDrawers.pop(id);
};
